/** @fileoverview Decides whether a hosted app page starts sign-in or handles the return from it. */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Button } from '../components/ui/button';
import LoadingState from '../components/ui/loading-state';
import AccountMenu from '../components/AccountMenu';
import AppFrame from '../components/AppRenderer/AppFrame';
import FabrikAppRenderer, { paletteStyle } from '../components/FabrikAppRenderer';
import { parseAppAddress } from './appAddress';
import { getHostedAppUrl } from './hostConfig';
import { takeStoredCheck } from './appCheck';
import AppNotFoundView from './AppNotFoundView';
import { clearAppPass, readAppPass, readAppPassExpiresAt, saveAppPass } from './appPass';
import { fetchPublishedApp } from './appRuntimeApi';
import type { PublishedAppResult } from './appRuntimeApi';
import { refreshAppSession, requestAppSession } from './appSessionApi';
import type { AppSessionResult } from './appSessionApi';
import RedirectToHandover from './RedirectToHandover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

type Props = {
  address: string;
};

type ReturnResult = { ok: true; code: string } | { ok: false };

/** Removes the code and check from the address bar and compares the check with the stored one. */
const readReturn = (): ReturnResult | null => {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  if (!code) {
    return null;
  }
  const check = params.get('check') ?? '';
  params.delete('code');
  params.delete('check');
  const rest = params.toString();
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash}`
  );
  const stored = takeStoredCheck();
  return check && check === stored ? { ok: true, code } : { ok: false };
};

let cachedReturn: ReturnResult | null | undefined;

/* Runs once per page load, so React Strict Mode's double render cannot read a cleaned address. */
const readReturnOnce = () => {
  if (cachedReturn === undefined) {
    cachedReturn = readReturn();
  }
  return cachedReturn;
};

let cachedSession: Promise<AppSessionResult> | undefined;

/* The code works once, so the call is cached for the page load and React Strict Mode's second effect reuses it. */
const requestSessionOnce = ({ address, code }: { address: string; code: string }) => {
  if (!cachedSession) {
    cachedSession = requestAppSession({ address, code });
  }
  return cachedSession;
};

const BOUNCE_KEY = 'fabrik-app-bounce';
const BOUNCE_WINDOW_MS = 30000;

/** Records an automatic sign-in restart and returns false when one already happened in the last 30 seconds. */
const claimBounce = () => {
  try {
    const last = Number(sessionStorage.getItem(BOUNCE_KEY) ?? '0');
    if (Date.now() - last < BOUNCE_WINDOW_MS) {
      return false;
    }
    sessionStorage.setItem(BOUNCE_KEY, String(Date.now()));
    return true;
  } catch {
    // Without storage the loop cannot be tracked, so do not restart automatically.
    return false;
  }
};

/** Shows a message with a button that starts sign-in again. */
const RetryView = ({
  message,
  onRetry,
  actionLabel = 'Try again',
}: {
  message: string;
  onRetry: () => void;
  actionLabel?: string;
}) => (
  <main className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
    <p className="text-sm text-destructive">{message}</p>
    <Button onClick={onRetry}>{actionLabel}</Button>
  </main>
);

/** Explains that only builders in the workspace can open a preview. */
const PreviewNotAvailableView = () => (
  <AppNotFoundView
    heading="Preview not available"
    text="Only people who build apps in this workspace can open the preview. Ask the app's builder for the published link."
  />
);

/** Loads the published app with the saved pass and renders it, or shows why it cannot be shown. */
const AppLoader = ({
  address,
  isPreview,
  onRestart,
}: {
  address: string;
  isPreview: boolean;
  onRestart: () => void;
}) => {
  const [result, setResult] = useState<PublishedAppResult>();
  const [stuck, setStuck] = useState(false);
  const [signedOut, setSignedOut] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [expiryWarningOpen, setExpiryWarningOpen] = useState(false);
  const [sessionExpiresAt, setSessionExpiresAt] = useState(readAppPassExpiresAt);
  const [now, setNow] = useState(Date.now());
  const [refreshingSession, setRefreshingSession] = useState(false);
  const [refreshError, setRefreshError] = useState('');
  const refreshingSessionRef = useRef(false);

  /** Clears the pass and restarts sign-in once, or shows the retry view when it already happened. */
  const handleSignedOut = useCallback(() => {
    clearAppPass();
    if (claimBounce()) {
      onRestart();
    } else {
      setStuck(true);
    }
  }, [onRestart]);
  const handleLogout = useCallback(() => {
    clearAppPass();
    setSignedOut(true);
  }, []);
  const handleSessionExpired = useCallback(() => {
    clearAppPass();
    setSessionExpired(true);
  }, []);
  const handleContinueWorking = useCallback(async () => {
    if (refreshingSessionRef.current) return;
    refreshingSessionRef.current = true;
    setRefreshingSession(true);
    setRefreshError('');
    try {
      const refreshed = await refreshAppSession({ address, token: readAppPass() });
      if (refreshed.kind === 'ok') {
        saveAppPass({ token: refreshed.token, expiresIn: refreshed.expiresIn });
        setSessionExpiresAt(readAppPassExpiresAt());
        setExpiryWarningOpen(false);
      } else if (refreshed.kind === 'expired') {
        handleSignedOut();
      } else {
        setRefreshError(refreshed.message);
      }
    } catch {
      setRefreshError('Unable to continue this session. Please try again.');
    } finally {
      refreshingSessionRef.current = false;
      setRefreshingSession(false);
    }
  }, [address, handleSignedOut]);

  useEffect(() => {
    if (!sessionExpiresAt) return;
    const warningDelay = sessionExpiresAt - Date.now() - 5 * 60_000;
    const expiryDelay = sessionExpiresAt - Date.now();
    if (warningDelay <= 0) setExpiryWarningOpen(true);
    const warningTimeout =
      warningDelay > 0 ? window.setTimeout(() => setExpiryWarningOpen(true), warningDelay) : 0;
    const expiryTimeout = window.setTimeout(
      () => {
        if (!refreshingSessionRef.current) handleSessionExpired();
      },
      Math.max(0, expiryDelay)
    );
    return () => {
      window.clearTimeout(warningTimeout);
      window.clearTimeout(expiryTimeout);
    };
  }, [handleSessionExpired, refreshingSession, sessionExpiresAt]);

  useEffect(() => {
    if (!expiryWarningOpen) return;
    setNow(Date.now());
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [expiryWarningOpen]);

  useEffect(() => {
    let active = true;
    fetchPublishedApp().then((outcome) => {
      if (!active) {
        return;
      }
      /*
       * A saved pass can name an app that was deleted and made again under the same address.
       * Sign in once more so the pass names the current app; a real "not found" then shows.
       */
      if (outcome.kind === 'notFound') {
        clearAppPass();
        if (claimBounce()) {
          onRestart();
        } else {
          setResult(outcome);
        }
        return;
      }
      if (outcome.kind === 'ok') {
        const currentSlug = parseAppAddress(address)?.slug;
        if (currentSlug && currentSlug !== outcome.app.slug) {
          const canonicalUrl = getHostedAppUrl({ slug: outcome.app.slug, preview: isPreview });
          if (canonicalUrl) {
            window.location.replace(
              `${canonicalUrl}${window.location.pathname}${window.location.search}${window.location.hash}`
            );
            return;
          }
        }
      }
      if (outcome.kind !== 'signedOut') {
        setResult(outcome);
        return;
      }
      handleSignedOut();
    });
    return () => {
      active = false;
    };
  }, [address, handleSignedOut, isPreview, onRestart]);

  const appName = result?.kind === 'ok' ? result.app.definition.app.name || result.app.name : '';
  const isPreviewApp = result?.kind === 'ok' && result.app.mode === 'preview';
  useEffect(() => {
    if (appName) {
      document.title = isPreviewApp ? `${appName} (preview)` : appName;
    }
  }, [appName, isPreviewApp]);

  const viewer = result?.kind === 'ok' ? result.app.viewer : undefined;
  const secondsLeft = Math.max(0, Math.ceil((sessionExpiresAt - now) / 1000));
  const timeLeft = `${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, '0')}`;
  const viewerName =
    [viewer?.firstName, viewer?.lastName].filter(Boolean).join(' ') || viewer?.email || '';
  const isEmbedded = window.parent !== window;
  const showLogout = !isEmbedded && !isPreview && !isPreviewApp;

  if (stuck) {
    return <RetryView message="We couldn't keep you signed in." onRetry={onRestart} />;
  }
  if (signedOut) {
    return (
      <RetryView message="You have signed out." actionLabel="Sign in again" onRetry={onRestart} />
    );
  }
  if (sessionExpired) {
    return (
      <RetryView
        message="Your session has expired. Sign in again to continue."
        actionLabel="Sign in again"
        onRetry={onRestart}
      />
    );
  }
  if (!result) {
    return (
      <main className="flex min-h-screen items-center justify-center p-6">
        <LoadingState>Loading the app…</LoadingState>
      </main>
    );
  }
  switch (result.kind) {
    case 'ok': {
      const appTheme = paletteStyle(result.app.definition.theme);
      const appUser = {
        first_name: viewer?.firstName ?? undefined,
        last_name: viewer?.lastName ?? undefined,
        email: viewer?.email ?? undefined,
        tenants: [],
      };
      return (
        <div className="flex h-screen flex-col">
          {isPreviewApp && (
            <div className="shrink-0 border-b bg-muted px-4 py-2 text-center text-xs text-muted-foreground">
              Preview of the draft. People using the app see the published version.
            </div>
          )}
          <AppFrame
            theme={{
              ...result.app.definition.theme,
              primaryColor:
                result.app.definition.theme?.primaryColor || result.app.appearance.primaryColor,
            }}
            appearance={result.app.appearance}
            screens={result.app.definition.screens}
            navigation={result.app.definition.navigation}
            tenantName={result.app.tenantName}
            appName={result.app.definition.app.name || result.app.name}
            accountSlot={
              <div className="ml-auto flex items-center gap-2">
                <AccountMenu
                  user={appUser}
                  name={viewerName}
                  closed={!showLogout}
                  logoutOnly={showLogout}
                  onLogout={handleLogout}
                />
              </div>
            }
          >
            <FabrikAppRenderer
              definition={result.app.definition}
              tenantId=""
              mode="hosted"
              definitionVersion={result.app.version ?? undefined}
              onSignedOut={handleSignedOut}
              viewer={viewer}
            />
          </AppFrame>
          <Dialog
            open={expiryWarningOpen}
            onOpenChange={(open) => {
              if (!open && !refreshingSession) void handleContinueWorking();
            }}
          >
            <DialogContent
              className="gap-4 p-5 sm:max-w-xl"
              style={
                {
                  '--primary': appTheme['--primary'],
                  '--primary-foreground': appTheme['--primary-foreground'],
                  '--secondary': appTheme['--secondary'],
                  '--secondary-foreground': appTheme['--secondary-foreground'],
                  '--background': appTheme['--background'],
                  '--foreground': appTheme['--foreground'],
                  '--card': appTheme['--card'],
                  '--card-foreground': appTheme['--card-foreground'],
                  '--popover': appTheme['--popover'],
                  '--popover-foreground': appTheme['--popover-foreground'],
                  '--muted': appTheme['--muted'],
                  '--muted-foreground': appTheme['--muted-foreground'],
                  '--border': appTheme['--border'],
                  '--input': appTheme['--input'],
                  '--ring': appTheme['--ring'],
                } as CSSProperties
              }
              showCloseButton={false}
            >
              <DialogHeader className="gap-3">
                <div className="flex items-center justify-between gap-4">
                  <DialogTitle className="text-xl font-bold">Session ending soon</DialogTitle>
                  <span
                    className="shrink-0 text-sm text-muted-foreground tabular-nums"
                    aria-label={`Session expires in ${timeLeft}`}
                  >
                    {timeLeft} remaining
                  </span>
                </div>
                <DialogDescription className="text-base leading-relaxed">
                  Select “Continue Working” to renew your session and stay signed in. If it expires,
                  you’ll need to sign in again.
                </DialogDescription>
              </DialogHeader>
              {refreshError && (
                <p className="text-base text-destructive" role="alert">
                  {refreshError}
                </p>
              )}
              <DialogFooter className="border-0 bg-transparent p-0">
                <Button
                  type="button"
                  variant="default"
                  className="text-base"
                  disabled={refreshingSession}
                  onClick={() => void handleContinueWorking()}
                >
                  {refreshingSession ? 'Continuing…' : 'Continue Working'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      );
    }
    case 'notPublished':
      return (
        <AppNotFoundView
          heading="This app hasn't been published yet."
          text="Check back once the person who built it has published it."
        />
      );
    case 'forbidden':
      return isPreview ? <PreviewNotAvailableView /> : <AppNotFoundView />;
    case 'notFound':
      return <AppNotFoundView />;
    default:
      return (
        <RetryView message={result.kind === 'error' ? result.message : ''} onRetry={onRestart} />
      );
  }
};

/** Swaps the code for a session and shows the result. */
const SessionView = ({
  address,
  isPreview,
  code,
  onRetry,
}: {
  address: string;
  isPreview: boolean;
  code: string;
  onRetry: () => void;
}) => {
  const [result, setResult] = useState<AppSessionResult>();

  useEffect(() => {
    let active = true;
    requestSessionOnce({ address, code }).then((outcome) => {
      if (outcome.kind === 'ok') {
        saveAppPass({ token: outcome.token, expiresIn: outcome.expiresIn });
      }
      if (active) {
        setResult(outcome);
      }
    });
    return () => {
      active = false;
    };
  }, [address, code]);

  if (!result) {
    return (
      <main className="flex min-h-screen items-center justify-center p-6">
        <LoadingState>Opening the app…</LoadingState>
      </main>
    );
  }
  switch (result.kind) {
    case 'ok':
      return <AppLoader address={address} isPreview={isPreview} onRestart={onRetry} />;
    case 'notFound':
      return <AppNotFoundView />;
    case 'previewForbidden':
      return <PreviewNotAvailableView />;
    case 'expired':
      return <RetryView message="This sign-in has expired." onRetry={onRetry} />;
    default:
      return <RetryView message={result.message} onRetry={onRetry} />;
  }
};

/** Starts sign-in, or handles the return with a code from the builder hand-over page. */
export default function AppModeRoot({ address }: Props) {
  const isPreview = parseAppAddress(address)?.isPreview ?? false;
  const [returned] = useState(readReturnOnce);
  const [restarted, setRestarted] = useState(false);
  const restart = useCallback(() => setRestarted(true), []);

  if (restarted) {
    return <RedirectToHandover address={address} />;
  }

  if (!returned) {
    return readAppPass() ? (
      <AppLoader address={address} isPreview={isPreview} onRestart={restart} />
    ) : (
      <RedirectToHandover address={address} />
    );
  }

  if (!returned.ok) {
    return <RetryView message="This sign-in didn't start here." onRetry={restart} />;
  }

  /* The code stays in memory only. */
  return (
    <SessionView address={address} isPreview={isPreview} code={returned.code} onRetry={restart} />
  );
}
